import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Building2, Trash2, Pencil, Save, Factory } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VendorMaster = () => {
  const [vendors, setVendors] = useState([]);
  const [plants, setPlants] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, vendor: null });
  const [plantAssignDialog, setPlantAssignDialog] = useState({ open: false, vendor: null, selectedPlants: [] });
  const [newVendor, setNewVendor] = useState({
    name: '',
    code: '',
    type: 'LOCAL',
    lead_time_days: 3,
    milk_run_freq: 'DAILY',
    quality_by: 'METALMAN',
    plant_ids: []
  });

  useEffect(() => {
    fetchVendors();
    fetchPlants();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchPlants = async () => {
    try {
      const response = await axios.get(`${API}/plants`);
      setPlants(response.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Error fetching plants:', error);
    }
  };

  const handleCreate = async () => {
    if (!newVendor.name || !newVendor.code) {
      toast.error('Name and code are required');
      return;
    }
    if (!newVendor.plant_ids || newVendor.plant_ids.length === 0) {
      toast.error('Please assign the vendor to at least one plant');
      return;
    }

    try {
      const vendorData = {
        ...newVendor,
        username: newVendor.code.toLowerCase().replace(/\s+/g, '_')
      };
      await axios.post(`${API}/vendors`, vendorData);
      toast.success('Vendor created successfully');
      setNewVendor({ name: '', code: '', type: 'LOCAL', lead_time_days: 3, milk_run_freq: 'DAILY', quality_by: 'METALMAN', plant_ids: [] });
      fetchVendors();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create vendor';
      toast.error(errorMsg);
    }
  };

  const handleDeleteClick = (vendor) => {
    setDeleteDialog({ open: true, vendor });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API}/vendors/${deleteDialog.vendor.id}`);
      toast.success('Vendor deleted successfully');
      setDeleteDialog({ open: false, vendor: null });
      fetchVendors();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete vendor';
      toast.error(errorMsg);
    }
  };

  const openPlantAssignDialog = (vendor) => {
    setPlantAssignDialog({
      open: true,
      vendor,
      selectedPlants: [...(vendor.plant_ids || [])]
    });
  };

  const handlePlantToggle = (plantId) => {
    setPlantAssignDialog(prev => {
      const selected = prev.selectedPlants.includes(plantId)
        ? prev.selectedPlants.filter(id => id !== plantId)
        : [...prev.selectedPlants, plantId];
      return { ...prev, selectedPlants: selected };
    });
  };

  const handleSavePlantAssignment = async () => {
    try {
      await axios.post(`${API}/vendors/${plantAssignDialog.vendor.id}/assign-plants`, {
        plant_ids: plantAssignDialog.selectedPlants
      });
      toast.success(`Vendor assigned to ${plantAssignDialog.selectedPlants.length} plant(s)`);
      setPlantAssignDialog({ open: false, vendor: null, selectedPlants: [] });
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign plants');
    }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="vendor-create-card">
        <CardHeader>
          <CardTitle>Add New Vendor</CardTitle>
          <CardDescription>Register a new vendor for procurement</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Vendor Name *</label>
              <Input
                data-testid="input-vendor-name"
                placeholder="Steel Works Ltd"
                value={newVendor.name}
                onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Code *</label>
              <Input
                data-testid="input-vendor-code"
                placeholder="SWL"
                value={newVendor.code}
                onChange={(e) => setNewVendor({ ...newVendor, code: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Type</label>
              <Select value={newVendor.type} onValueChange={(value) => setNewVendor({ ...newVendor, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOCAL">Local</SelectItem>
                  <SelectItem value="REMOTE">Remote</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Lead Time (days)</label>
              <Input
                data-testid="input-vendor-lead-time"
                type="number"
                value={newVendor.lead_time_days}
                onChange={(e) => setNewVendor({ ...newVendor, lead_time_days: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Milk Run Frequency</label>
              <Select value={newVendor.milk_run_freq} onValueChange={(value) => setNewVendor({ ...newVendor, milk_run_freq: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DAILY">Daily</SelectItem>
                  <SelectItem value="WEEKLY">Weekly</SelectItem>
                  <SelectItem value="BIWEEKLY">Bi-weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Quality By</label>
              <Select value={newVendor.quality_by} onValueChange={(value) => setNewVendor({ ...newVendor, quality_by: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="VENDOR">Vendor</SelectItem>
                  <SelectItem value="METALMAN">Metalman (In-house)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Plant Assignment for Create */}
            <div>
              <label className="text-sm font-medium mb-2 block">Assign to Plants *</label>
              <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto bg-white">
                {plants.map(plant => (
                  <label key={plant.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <Checkbox
                      data-testid={`create-vendor-plant-${plant.id}`}
                      checked={newVendor.plant_ids.includes(plant.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewVendor(prev => ({ ...prev, plant_ids: [...prev.plant_ids, plant.id] }));
                        } else {
                          setNewVendor(prev => ({ ...prev, plant_ids: prev.plant_ids.filter(id => id !== plant.id) }));
                        }
                      }}
                    />
                    <span className="text-sm">{plant.code} - {plant.name}</span>
                  </label>
                ))}
              </div>
              {newVendor.plant_ids.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">Select at least one plant</p>
              )}
            </div>
          </div>
          <Button data-testid="create-vendor-btn" onClick={handleCreate} className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            Create Vendor
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="vendor-list-card">
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              All Vendors ({vendors.length})
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Plants</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Lead Time</TableHead>
                <TableHead>Milk Run</TableHead>
                <TableHead>Quality By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No vendors found for this plant. Create a new vendor or extend existing vendors to this plant.
                  </TableCell>
                </TableRow>
              ) : (
                vendors.map((vendor) => (
                  <TableRow key={vendor.id} data-testid={`vendor-row-${vendor.id}`}>
                    <TableCell className="font-medium">{vendor.name}</TableCell>
                    <TableCell>{vendor.code}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {vendor.plant_ids && vendor.plant_ids.length > 0 ? (
                          vendor.plant_ids.map(pid => {
                            const plant = plants.find(p => p.id === pid);
                            return plant ? (
                              <Badge key={pid} variant="outline" className="bg-teal-50 text-teal-700 text-xs whitespace-nowrap">
                                {plant.code}
                              </Badge>
                            ) : null;
                          })
                        ) : (
                          <Badge variant="outline" className="bg-red-50 text-red-600 text-xs">None</Badge>
                        )}
                        <button
                          onClick={() => openPlantAssignDialog(vendor)}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 hover:bg-blue-100 text-slate-500 hover:text-blue-600 transition-colors ml-0.5"
                          title="Manage plant assignment"
                          data-testid={`assign-plant-vendor-${vendor.id}`}
                        >
                          <Pencil className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={vendor.type === 'LOCAL' ? 'default' : 'secondary'}>{vendor.type}</Badge>
                    </TableCell>
                    <TableCell>{vendor.lead_time_days} days</TableCell>
                    <TableCell>{vendor.milk_run_freq}</TableCell>
                    <TableCell>{vendor.quality_by}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteClick(vendor)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Plant Assignment Dialog */}
      <Dialog open={plantAssignDialog.open} onOpenChange={(open) => !open && setPlantAssignDialog({ open: false, vendor: null, selectedPlants: [] })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5 text-teal-600" />
              Assign Vendor to Plants
            </DialogTitle>
            <DialogDescription>
              {plantAssignDialog.vendor && (
                <span>Select plants for <strong>{plantAssignDialog.vendor.name}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            {plants.map(plant => (
              <label
                key={plant.id}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                data-testid={`vendor-assign-checkbox-${plant.id}`}
              >
                <Checkbox
                  checked={plantAssignDialog.selectedPlants.includes(plant.id)}
                  onCheckedChange={() => handlePlantToggle(plant.id)}
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">{plant.name}</div>
                  <div className="text-xs text-slate-500">Code: {plant.code} | {plant.location}</div>
                </div>
                <Badge variant="outline" className="text-xs font-mono">{plant.code}</Badge>
              </label>
            ))}
            {plantAssignDialog.selectedPlants.length === 0 && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                Warning: Vendor will not be visible in any plant if no plants are selected.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlantAssignDialog({ open: false, vendor: null, selectedPlants: [] })}>
              Cancel
            </Button>
            <Button
              data-testid="save-vendor-plant-assignment"
              onClick={handleSavePlantAssignment}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save ({plantAssignDialog.selectedPlants.length} plants)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, vendor: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete vendor <strong>{deleteDialog.vendor?.name}</strong>? 
              This action cannot be undone. The vendor cannot be deleted if it's referenced in any purchase orders or item sourcing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorMaster;
